import type { BriefDepth } from "@/shared/brief/run-store";
import {
  accountScopedStorageKey,
  readAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import {
  MARKET_COUNTRY_OPTIONS,
  PRODUCT_LANGUAGE_OPTIONS,
  restoredProductLanguage,
  type MarketCountryCode,
  type ProductLanguageCode,
} from "./experience-catalog.mjs";
import { browserMarketExperience } from "./browser-market-experience.mjs";

const FIRST_RUN_KEY = "tb_public_web_first_run_v1";
const FIRST_RUN_EVENT = "tb-public-web-first-run-changed";

export const ONBOARDING_COUNTRIES = MARKET_COUNTRY_OPTIONS;
export const ONBOARDING_LANGUAGES = PRODUCT_LANGUAGE_OPTIONS;

export type OnboardingCountryCode = MarketCountryCode;
export type OnboardingLanguageCode = ProductLanguageCode;

export type FirstRunState = {
  completed: boolean;
  country: OnboardingCountryCode;
  lang: OnboardingLanguageCode;
  localCity: string;
  preferredDisplayName: string;
  briefDepth: BriefDepth;
  completedAtEpochMs: number | null;
};

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function normalizeText(value: unknown, maxLength: number): string {
  return String(value || "")
    .trim()
    .slice(0, maxLength);
}

function isOnboardingCountry(value: unknown): value is OnboardingCountryCode {
  return ONBOARDING_COUNTRIES.some((country) => country.code === value);
}

function isOnboardingLanguage(value: unknown): value is OnboardingLanguageCode {
  return ONBOARDING_LANGUAGES.some((language) => language.code === value);
}

function normalizeDepth(value: unknown): BriefDepth {
  if (value === "focused" || value === "deep") return value;
  return "balanced";
}

function countrySupportsLanguage(
  country: OnboardingCountryCode,
  lang: OnboardingLanguageCode,
): boolean {
  const languages = countryConfig(country).languages as readonly OnboardingLanguageCode[];
  return languages.includes(lang);
}

function normalizeState(value: Partial<FirstRunState> | null | undefined): FirstRunState {
  const browserExperience = browserMarketExperience();
  const country = isOnboardingCountry(value?.country) ? value.country : browserExperience.country;
  const restoredLang = restoredProductLanguage(value?.lang);
  const preferredLang =
    restoredLang && countrySupportsLanguage(country, restoredLang)
      ? restoredLang
      : country === browserExperience.country &&
          isOnboardingLanguage(browserExperience.lang) &&
          countrySupportsLanguage(country, browserExperience.lang)
        ? browserExperience.lang
        : "en";
  return {
    completed: value?.completed === true,
    country,
    lang: preferredLang,
    localCity: normalizeText(value?.localCity, 80),
    preferredDisplayName: normalizeText(value?.preferredDisplayName, 80),
    briefDepth: normalizeDepth(value?.briefDepth),
    completedAtEpochMs:
      typeof value?.completedAtEpochMs === "number" && Number.isFinite(value.completedAtEpochMs)
        ? value.completedAtEpochMs
        : null,
  };
}

export function countryConfig(country: OnboardingCountryCode) {
  return ONBOARDING_COUNTRIES.find((item) => item.code === country) || ONBOARDING_COUNTRIES[1];
}

export function loadFirstRunState(): FirstRunState {
  const store = storage();
  if (!store) return normalizeState(null);
  try {
    const raw = readAccountScopedStorage(FIRST_RUN_KEY);
    return normalizeState(raw ? JSON.parse(raw) : null);
  } catch {
    return normalizeState(null);
  }
}

export function saveFirstRunState(state: Partial<FirstRunState>): FirstRunState {
  const next = normalizeState({ ...loadFirstRunState(), ...state });
  const store = storage();
  if (store) {
    try {
      writeAccountScopedStorage(FIRST_RUN_KEY, JSON.stringify(next));
    } catch {
      /* First-run persistence is best effort. */
    }
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(FIRST_RUN_EVENT, { detail: { firstRun: next } }));
  }
  return next;
}

export function completeFirstRun(state: Partial<FirstRunState>): FirstRunState {
  return saveFirstRunState({
    ...state,
    completed: true,
    completedAtEpochMs: Date.now(),
  });
}

export function onFirstRunChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (event: StorageEvent) => {
    try {
      if (event.storageArea && event.storageArea !== window.localStorage) return;
      if (event.key === null || event.key === accountScopedStorageKey(FIRST_RUN_KEY)) listener();
    } catch {
      // Storage denial or an unreadable auth scope must not break other listeners.
    }
  };
  window.addEventListener(FIRST_RUN_EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(FIRST_RUN_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}
